import { IconArrowLeft, IconArrowRight, IconSearch, IconUser } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useProveedoresBuscar } from "@/features/proveedor-manual/hooks/use-proveedores-buscar";
import { useSeleccionarProveedorManual } from "@/features/proveedor-manual/hooks/use-seleccionar-proveedor-manual";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Badge, Button, Card, Group, Input, Loader, PageHeader, PaginadorTabla, ReadOnlyBanner, Stack, Text, ThemeIcon } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/proveedor-manual";

const PAGE_SIZE = 15;

/**
 * P44 · FB60 Contrapago · selección de proveedor sin CFDI del que derivar el
 * RFC (a diferencia del flujo normal, que agrupa candidatos por RFC emisor) ·
 * picker con búsqueda server-side sobre el catálogo global de proveedores activos.
 */
export function ProveedorManualPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);

  const esDueno = useEsDueno(sol);
  const puedeEditar = esDueno && (sol.estado === "SeleccionandoProveedor" || sol.estado === "CapturandoDatos");

  const [texto, setTexto] = useState("");
  const [page, setPage] = useState(1);
  const [textoDebounced] = useDebouncedValue(texto, 350);
  useEffect(() => setPage(1), [textoDebounced]);

  const { data, isFetching } = useProveedoresBuscar({ texto: textoDebounced.trim() || undefined, page, pageSize: PAGE_SIZE });

  const [seleccionado, setSeleccionado] = useState<number | null>(sol.proveedor?.numeroProveedor ?? null);
  const asignar = useSeleccionarProveedorManual(id);

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Proveedor"
        description="FB60 Contrapago · busca y selecciona el proveedor al que se le pagará."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Proveedor" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="proveedor" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {!puedeEditar && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="El proveedor asignado no puede modificarse." />}
          {sol.proveedor && (
            <Card padding="md" style={{ borderColor: "var(--color-multiva-400)", background: "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)" }}>
              <Text size="sm">
                <Text span fw={600}>
                  Proveedor asignado:{" "}
                </Text>
                #{sol.proveedor.numeroProveedor} · {sol.proveedor.razonSocial} · {sol.proveedor.rfc}
              </Text>
            </Card>
          )}
          {puedeEditar && (
            <Card padding="lg">
              <Stack gap="md">
                <Input
                  label="Buscar proveedor"
                  placeholder="Razón social, RFC o número de proveedor"
                  leftSection={<IconSearch size={16} />}
                  rightSection={isFetching ? <Loader size="xs" /> : null}
                  value={texto}
                  onChange={(e) => setTexto(e.currentTarget.value)}
                />
                <Stack gap={6}>
                  {(data?.items ?? []).map((p) => {
                    const activo = seleccionado === p.numeroProveedor;
                    return (
                      <button
                        key={p.numeroProveedor}
                        type="button"
                        onClick={() => setSeleccionado(p.numeroProveedor)}
                        className="rounded-lg border p-3 text-left transition-colors duration-120"
                        style={{
                          borderColor: activo ? "var(--color-multiva-500)" : "var(--color-border)",
                          backgroundColor: activo ? "color-mix(in srgb, var(--color-multiva-600) 8%, transparent)" : "var(--color-background)",
                          opacity: p.estaActivo ? 1 : 0.5,
                        }}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <ThemeIcon variant={activo ? "filled" : "light"} color="multiva" size="md">
                            <IconUser size={16} stroke={1.6} />
                          </ThemeIcon>
                          <Stack gap={0} className="min-w-0 flex-1">
                            <Group gap={6}>
                              <Text fw={600} size="sm" truncate>
                                {p.razonSocial}
                              </Text>
                              <Badge variant="light" color="slate" size="xs">
                                #{p.numeroProveedor}
                              </Badge>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {p.rfc}
                            </Text>
                          </Stack>
                        </Group>
                      </button>
                    );
                  })}
                  {data && data.items.length === 0 && (
                    <Text size="sm" c="dimmed">
                      Sin proveedores para esa búsqueda.
                    </Text>
                  )}
                </Stack>
                {data && <PaginadorTabla total={data.total} page={data.page} pageSize={data.pageSize} onPageChange={setPage} sustantivo="proveedores" />}
              </Stack>
            </Card>
          )}
          <Group justify="space-between">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate({ to: "/solicitudes/$id/datos-generales", params: { id } })}>
              Regresar a Datos generales
            </Button>
            {puedeEditar && (
              <Button
                rightSection={<IconArrowRight size={14} />}
                disabled={!seleccionado}
                loading={asignar.isPending}
                onClick={() =>
                  seleccionado &&
                  asignar.mutate(seleccionado, {
                    onSuccess: () => navigate({ to: "/solicitudes/$id/prorrateo-sin-cfdi", params: { id } }),
                  })
                }
              >
                {asignar.isPending ? "Asignando…" : "Asignar y continuar"}
              </Button>
            )}
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
